import styled from '@emotion/styled'
import * as React from 'react'

export type Column<T> = {
  align?: 'left' | 'right' | 'center'
  'data-testid'?: string
  key?: boolean
  name: keyof T
  render?: (item?: T, data?: T[]) => React.ReactNode
  renderHeader?: (data?: T[]) => React.ReactNode
  title?: string
  width?: string
}

export type ResultTableProps<T> = {
  columns: Column<T>[]
  data?: T[]
  highlightRow?: (row: T) => string
}

type CellProps = {
  align?: 'left' | 'right' | 'center'
  index: number
  length: number
  width?: string
}

const StyledTable = styled.table`
  width: 100%;
  border-collapse: separate;
`

const StyledTh = styled.th<CellProps>`
  ${props => props.width || ''};
  text-align: ${props => (props.align ? props.align : 'left')};
  ${props => props.index === 0 && 'border-left: none'};
  ${props => props.index === props.length && 'border-right: none'};
  padding: 24px 12px;
  background-color: var(--White96);
  color: var(--Graphite);
  border-top: 1px solid var(--White90);
  border-bottom: 1px solid var(--White90);
  position: sticky;
  top: 0;
`

const StyledTd = styled.td<CellProps>`
  ${props => props.width || ''};
  text-align: ${props => (props.align ? props.align : 'left')};
  border-left: 1px solid var(--White96);
  border-bottom: 1px solid var(--White96);
  padding: 24px 12px;
  &:first-of-type {
    border-left: none;
  }
`

export function ResultTable<T>(props: ResultTableProps<T>) {
  const { columns, data, highlightRow } = props

  return (
    <StyledTable>
      <thead>
        <tr>
          {columns.map((col, i) => (
            <StyledTh
              key={`th-${col.title}`}
              align={col.align}
              index={i}
              length={columns.length}
              width={col.width}
            >
              {col.renderHeader ? col.renderHeader(data) : col.title}
            </StyledTh>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.map((d, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <tr key={i} className={highlightRow ? highlightRow(d) : ''}>
            {columns.map((c, j) => {
              if (c.render) {
                return (
                  <StyledTd
                    // eslint-disable-next-line react/no-array-index-key
                    key={`td-${i}-${j}`}
                    align={c.align}
                    index={i}
                    length={columns.length}
                    width={c.width}
                  >
                    {c.render(d, data)}
                  </StyledTd>
                )
              }
              return (
                <StyledTd
                  // eslint-disable-next-line react/no-array-index-key
                  key={`td-${i}-${j}`}
                  align={c.align}
                  index={i}
                  length={columns.length}
                  width={c.width}
                >
                  {String(d[c.name])}
                </StyledTd>
              )
            })}
          </tr>
        ))}
      </tbody>
    </StyledTable>
  )
}
